import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, Trash2, RotateCcw } from "lucide-react";
import { inr, getApiErrorMessage } from "@/lib/utils_pharma";
import api from "@/lib/api";
import { toast } from "sonner";

export default function DeleteBillDialog({ open, onOpenChange, bill, onSuccess }) {
  const [deleting, setDeleting] = useState(false);
  const [salesReturns, setSalesReturns] = useState([]);
  const [checkingReturns, setCheckingReturns] = useState(false);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);

  const isDevDeleteMode = process.env.REACT_APP_ENABLE_BILL_DELETE !== "false";

  useEffect(() => {
    if (!open || !bill) {
      setSalesReturns([]);
      setShowRollbackConfirm(false);
      return;
    }

    let active = true;
    const fetchReturns = async () => {
      setCheckingReturns(true);
      try {
        const targetId = bill.id || bill.bill_no;
        const res = await api.get("/sales-returns", { params: { bill_id: targetId } });
        if (active) {
          const returns = res.data || [];
          setSalesReturns(returns);
          if (returns.length > 0 && isDevDeleteMode) {
            setShowRollbackConfirm(true);
          }
        }
      } catch (err) {
        // Fallback: silently ignore
      } finally {
        if (active) setCheckingReturns(false);
      }
    };

    fetchReturns();
    return () => {
      active = false;
    };
  }, [open, bill, isDevDeleteMode]);

  if (!bill) return null;

  const targetId = bill.id || bill.bill_no;
  const hasReturns = salesReturns.length > 0;

  // Normal delete
  const handleDelete = async () => {
    if (deleting || !bill) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/bills/${targetId}`);
      toast.success(res.data?.message || "Bill deleted and inventory restored successfully.");
      onOpenChange(false);
      if (onSuccess) onSuccess(bill);
    } catch (err) {
      const msg = getApiErrorMessage(err, "Failed to delete bill");
      if (msg.includes("Sales Return") && isDevDeleteMode) {
        setShowRollbackConfirm(true);
      } else {
        toast.error(msg);
      }
    } finally {
      setDeleting(false);
    }
  };

  // Safe Test Rollback
  const handleRollback = async () => {
    if (deleting || !bill) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/bills/${targetId}?rollback=true`);
      toast.success(res.data?.message || "Test bill and associated returns rolled back successfully.");
      onOpenChange(false);
      if (onSuccess) onSuccess(bill);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to rollback test bill"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!deleting) onOpenChange(val);
      }}
    >
      <DialogContent className="max-w-md" data-testid="delete-bill-modal">
        {showRollbackConfirm && isDevDeleteMode ? (
          // Dev / Admin Rollback Test Bill Confirmation
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600 font-semibold">
                <AlertTriangle size={20} className="shrink-0 text-amber-600" />
                <span>Rollback Test Bill?</span>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2 text-sm">
              <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground uppercase font-semibold">Invoice</span>
                  <span className="font-mono font-bold text-foreground text-sm" data-testid="delete-modal-bill-no">
                    {bill.bill_no}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground uppercase font-semibold">Amount</span>
                  <span className="font-mono font-bold text-foreground text-sm" data-testid="delete-modal-amount">
                    {inr(bill.grand_total)}
                  </span>
                </div>
                {salesReturns.length > 0 && (
                  <div className="flex justify-between items-center text-xs pt-1 border-t border-amber-200/60">
                    <span className="text-amber-800 uppercase font-semibold">Associated Returns</span>
                    <span className="font-semibold text-amber-900">
                      {salesReturns.length} {salesReturns.length === 1 ? "return record" : "return records"}
                    </span>
                  </div>
                )}
              </div>

              <div className="rounded-md border border-amber-200 bg-amber-50/70 p-3 text-amber-950 text-xs leading-relaxed space-y-1.5">
                <p>
                  This invoice has Sales Returns. Rolling it back will reverse its return/exchange inventory effects, restore the original sale stock, remove the Sales Return, and delete the test invoice.
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                disabled={deleting}
                onClick={() => onOpenChange(false)}
                data-testid="cancel-rollback-bill"
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={deleting}
                onClick={handleRollback}
                data-testid="confirm-rollback-bill"
                className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white font-medium"
              >
                {deleting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span>Rolling back...</span>
                  </>
                ) : (
                  <>
                    <RotateCcw size={14} />
                    <span>Rollback Test Bill</span>
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          // Standard Delete Bill Confirmation (or Production Block)
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive font-semibold">
                <AlertTriangle size={18} />
                <span>Delete Bill?</span>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2 text-sm">
              <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground uppercase font-semibold">Invoice</span>
                  <span className="font-mono font-bold text-foreground text-sm" data-testid="delete-modal-bill-no">
                    {bill.bill_no}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground uppercase font-semibold">Amount</span>
                  <span className="font-mono font-bold text-foreground text-sm" data-testid="delete-modal-amount">
                    {inr(bill.grand_total)}
                  </span>
                </div>
              </div>

              {hasReturns && !isDevDeleteMode ? (
                <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-destructive text-xs leading-relaxed">
                  This bill has a Sales Return and cannot be deleted directly.
                </div>
              ) : (
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Deleting this bill will permanently remove the invoice and restore the sold medicines to their original inventory batches. Sales reports and dashboard totals will also be updated.
                </p>
              )}
            </div>

            <DialogFooter className="gap-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                disabled={deleting}
                onClick={() => onOpenChange(false)}
                data-testid="cancel-delete-bill"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={deleting || (hasReturns && !isDevDeleteMode)}
                onClick={handleDelete}
                data-testid="confirm-delete-bill"
                className="gap-1.5"
              >
                {deleting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={14} />
                    <span>Delete Bill</span>
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
